import { Modal } from "@mui/material";
import { cloneDeep, get, set } from "lodash";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import CtaComponent from "../../../components/reusable-ui/cta";
import CustomDropdown from "../../../components/reusable-ui/custom-dropdown";
import SwitchComponent from "../../../components/reusable-ui/switch";
import CustomTextField from "../../../components/reusable-ui/textfield-box";
import ObjectID from "bson-objectid";
import {
  BUSINESS_DAYS_NAME_MAP,
  CTA_SIZE,
  CTA_TYPE,
} from "../../../data/constants";
import {
  AGGREGATOR_TYPE,
  AGGREGATOR_CONFIG_MADATORY_FIELDS_MAP,
  WEBHOOK_SOURCE_TYPE,
} from "../../../data/outlet-status-constants";
import { convertArrayToObject } from "../../../helpers/catalog-helper";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import {
  getAllWebhookEntities,
  getOutletConfiguration,
  getOutletsData,
  getPaymentProviders,
  saveOutletConfiguration,
} from "../../../redux/slices";
import { allowEverything } from "../../../utils";
import RistaPreferences from "./rista-preferences";
import "./style.scss";
import { TimelineManagementModal } from "../../catalog-management";

interface Props {
  onModalClose: any;
  moreOptionsMenuData?: any;
  isEditMode?: boolean;
  setApiStateModalData: any;
}

const OutletConfigurationEditModal = (props: Props) => {
  const {
    onModalClose,
    moreOptionsMenuData,
    isEditMode = false,
    setApiStateModalData,
  } = props;
  const outletData = moreOptionsMenuData?.outletData || {};
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMsgMap, setErrMsgMap] = useState<any>({});
  const [aggregatorConfig, setAggregatorConfig] = useState<any>({
    active: 0,
    autoProcess: 0,
    autoConfirm: 0,
  });
  const [timeSlotEditModalData, setTimeSlotEditModalData] = useState<any>({
    show: false,
    day: "",
    slots: [],
    isEditMode: false,
  });
  const [metaProvidersDataMap, setMetaProvidersDataMap] = useState<any>({});
  const [paymentProvidersMap, setPaymentProvidersMap] = useState<any>({});
  const [paymentInfoList, setPaymentInfoList] = useState<any>([]);
  const {
    allChannelsDefDataMap,
    businessBranchesDataMap,
    allWebhookEntitiesMap,
    paymentProviders,
    outletsData,
  } = useSelector((state: any) => ({
    allChannelsDefDataMap: state.businessReducer?.allChannelsDefDataMap,
    businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
    allWebhookEntitiesMap: convertArrayToObject(
      state.outletStatusReducer.allWebhookEntities,
      "mid"
    ),
    paymentProviders: state.outletStatusReducer.paymentProviders,
    outletsData: state.outletStatusReducer.outletsData,
  }));
  const dispatch = useDispatch();
  const MANDATORY_TEXT = "This field is mandatory.";

  const DELIVERY_MODES: any = {
    pickup: {
      id: "pickup",
      name: "Pickup",
    },
    delivery: {
      id: "delivery",
      name: "Delivery",
    },
    "in store": {
      id: "in store",
      name: "In Store",
    },
  };

  const TEXT_FIELD_CONFIG = {
    outletId: {
      label: "Outlet ID",
      placeholder: "Enter Outlet ID",
      regexCheck: allowEverything,
      customClass: "",
      parentClass: "mb-[6px]",
      value: aggregatorConfig?.id,
      key: "id",
      maxLength: 25,
      isDisabled: true,
    },
    externalOutletId: {
      label: "External Outlet ID",
      placeholder: "Enter External Outlet ID",
      regexCheck: allowEverything,
      customClass: "",
      parentClass: "mb-[6px]",
      value: aggregatorConfig?.extId,
      key: "extId",
      maxLength: 25,
    },
    externalOutletUrl: {
      label: "External Outlet URL",
      placeholder: "Enter External Outlet URL",
      regexCheck: allowEverything,
      customClass: "",
      parentClass: "mb-[6px]",
      value: aggregatorConfig?.extURL,
      key: "extURL",
      maxLength: 40,
    },
    apiKey: {
      label: "API Key",
      placeholder: "Enter API Key",
      regexCheck: allowEverything,
      customClass: "",
      parentClass: "mb-[6px]",
      value: aggregatorConfig?.apiKey,
      key: "apiKey",
      maxLength: 100,
    },
  };

  const DROPDOWN_CONFIGS = {
    branch: {
      label: "Branch",
      optionsMap: businessBranchesDataMap || {},
      placeholder: "Select Branch",
      uniqueKey: "branchId",
      dropdownHeading: "Branches",
      searchPlaceholder: "Search a branch",
      parentClass: "w-full",
      key: "brid",
      preSelectedValues: aggregatorConfig?.brid,
      onSubmit: (value: any) => handleInputChange(value[0], "brid"),
      getListItemText: (data: any) => data?.branchName,
      isDisabled: isEditMode,
    },
    channel: {
      label: "Channel",
      optionsMap: allChannelsDefDataMap || {},
      placeholder: "Select Channel",
      uniqueKey: "channelDefId",
      dropdownHeading: "Channels",
      searchPlaceholder: "Search a channel",
      parentClass: "w-full",
      key: "channelDefId",
      preSelectedValues: aggregatorConfig?.channelDefId,
      onSubmit: (value: any) => handleInputChange(value[0], "channelDefId"),
      getListItemText: (data: any) => data?.name,
    },
    webhookEntity: {
      label: "Webhook",
      optionsMap: allWebhookEntitiesMap || {},
      placeholder: "Select Webhook",
      uniqueKey: "mid",
      dropdownHeading: "Webhook",
      searchPlaceholder: "Search a webhook",
      parentClass: "w-full",
      key: "webhookId",
      preSelectedValues: aggregatorConfig?.webhookId,
      onSubmit: (value: any) => handleInputChange(value[0], "webhookId"),
      getListItemText: (data: any) => data?.label,
    },
    webhookSource: {
      label: "Webhook Source",
      optionsMap: WEBHOOK_SOURCE_TYPE || {},
      placeholder: "Select Webhook Source",
      uniqueKey: "key",
      dropdownHeading: "Webhook Sources",
      searchPlaceholder: "Search a webhook source",
      parentClass: "w-full",
      key: "webhookSourceType",
      preSelectedValues:
        aggregatorConfig?.webhookSourceType?.toLowerCase() || "",
      onSubmit: (value: any) =>
        handleInputChange(
          WEBHOOK_SOURCE_TYPE[value[0]]?.id,
          "webhookSourceType"
        ),
      getListItemText: (data: any) => data?.label,
    },
  };

  useEffect(() => {
    if (!isEditMode && aggregatorConfig?.type) {
      let outletId = "";
      if (aggregatorConfig.type === AGGREGATOR_TYPE.rista.id) {
        outletId = ObjectID().toHexString();
      } else {
        outletId =
          aggregatorConfig.type + "_" + ObjectID().toHexString().slice(0, 13);
      }
      handleInputChange(outletId, "id");
    }
  }, [aggregatorConfig?.type]);

  useEffect(() => {
    if (isEditMode && outletData?.id) {
      const callback = (success: boolean, response: any = null) => {
        if (success) {
          let paymentInfoMap = response?.preferences?.paymentInfo
            ? convertArrayToObject(response?.preferences?.paymentInfo, "mid")
            : {};
          setPaymentInfoList(Object.keys(paymentInfoMap));
          setAggregatorConfig(response);
        }
      };
      dispatch(
        getOutletConfiguration({
          outletId: outletData?.id,
          callback,
        })
      );
    }
  }, [outletData?.id]);

  useEffect(() => {
    const callback = (success: boolean, response: any) => {
      if (success) {
      }
    };
    dispatch(getAllWebhookEntities({ callback }));
    dispatch(
      getPaymentProviders({
        dataToPost: {
          access: "pwa",
          branchId: null,
        },
      })
    );
  }, []);

  useEffect(() => {
    if (outletsData?.metaProviders?.length) {
      let dataToUpdate = convertArrayToObject(outletsData.metaProviders, "mid");
      setMetaProvidersDataMap(dataToUpdate);
    }
  }, [outletsData?.metaProviders]);

  useEffect(() => {
    let dataToUpdate: any = {};
    if (paymentProviders?.length) {
      dataToUpdate = convertArrayToObject(paymentProviders, "mid") || {};
    }
    dataToUpdate.cash = {
      mid: "cash",
      name: "Cash",
    };
    setPaymentProvidersMap(dataToUpdate);
  }, [paymentProviders]);

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(aggregatorConfig);
    set(dataToUpdate, fieldToUpdate, value);
    setAggregatorConfig(dataToUpdate);
    setIsChangesMade(true);
  };

  function fetchAllOutletsData() {
    const dataToPost = {
      aggregatorType: "Order",
      branches: "all",
      // isNew: true,
    };
    dispatch(getOutletsData({ dataToPost }));
  }

  const tConvert = ({ hh, mm }: any, isEndTime: boolean = false) => {
    let hrsNum = Number(hh),
      minsNum = Number(mm);
    if (isEndTime) {
      minsNum = minsNum + 1;
      if (minsNum >= 60) {
        minsNum -= 60;
        hrsNum += 1;
      }
      if (hrsNum >= 24) {
        hrsNum -= 24;
      }
    }

    let suf = "AM";
    if (hrsNum >= 12) {
      suf = "PM";
    }

    if (hrsNum > 12) {
      hrsNum -= 12;
    }

    if (hrsNum === 0) {
      hrsNum = 12;
    }
    return `${hrsNum.toLocaleString("en-US", {
      minimumIntegerDigits: 2,
    })}:${minsNum.toLocaleString("en-US", { minimumIntegerDigits: 2 })} ${suf}`;
  };

  const onTimeSlotEditIconClick = (day: string, slots: any) => {
    setTimeSlotEditModalData({ show: true, isEditMode: true, day, slots });
  };

  const onTimeSlotEditModalClose = () => {
    setTimeSlotEditModalData({ show: false });
  };

  const onTimeSlotEditModalSave = (timeSlots: any, selectedDays: any) => {
    let key =
      aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id
        ? "preferences.businessHours.businessDays"
        : "businessHours";
    let businessDaysList = cloneDeep(get(aggregatorConfig, key)) || [];
    let businessDaysMap = convertArrayToObject(businessDaysList, "day");

    let dataToUpdate = [];
    for (let businessDay of Object.keys(BUSINESS_DAYS_NAME_MAP)) {
      if (!selectedDays[businessDay] && !businessDaysMap[businessDay]) {
        continue;
      }

      if (!selectedDays[businessDay]) {
        dataToUpdate.push(businessDaysMap[businessDay]);
      } else {
        let businessDayData: any = {
          slots: timeSlots,
          day: BUSINESS_DAYS_NAME_MAP[businessDay].shortName,
        };
        dataToUpdate.push(businessDayData);
      }
    }
    handleInputChange(dataToUpdate, key);
    onTimeSlotEditModalClose();
  };

  const onTimeSlotDeleteIconClick = (day: string) => {
    let key =
      aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id
        ? "preferences.businessHours.businessDays"
        : "businessHours";
    let businessDaysList: any = cloneDeep(get(aggregatorConfig, key)) || [];
    let dataToUpdate = businessDaysList.filter(
      (businessDay: any) => day !== businessDay?.day
    );
    handleInputChange(dataToUpdate, key);
  };

  const validateAggregatorData = () => {
    let data = cloneDeep(aggregatorConfig) || {};
    let paymentInfoData: any = [];
    if (data?.type === AGGREGATOR_TYPE.rista.id) {
      for (let id of paymentInfoList) {
        if (id === "cash") {
          paymentInfoData.push({ mid: "Cash", name: "Cash" });
        } else {
          paymentInfoData.push(paymentProvidersMap[id]);
        }
      }
      set(data, "preferences.paymentInfo", paymentInfoData);
    }
    if (data?.type === AGGREGATOR_TYPE.rista.id) {
      let deliveryModes: any = data?.preferences?.deliveryModes
        ? data?.preferences?.deliveryModes.map(
            (id: string) => DELIVERY_MODES[id]?.name
          )
        : [];
      set(data, "preferences.deliveryModes", deliveryModes);
    }
    let isValid = true;
    let errorMessageMap: any = {};
    Object.values(AGGREGATOR_CONFIG_MADATORY_FIELDS_MAP[data?.type]).forEach(
      (fieldObj: any) => {
        let val = get(data, fieldObj?.key);
        if (
          (fieldObj?.type === "string" && !val) ||
          (fieldObj?.type === "array" && !val?.length)
        ) {
          errorMessageMap[fieldObj?.key] = MANDATORY_TEXT;
          isValid = false;
        }
      }
    );

    if (
      data?.type === AGGREGATOR_TYPE.rista.id &&
      !get(data, "preferences.contactInfo.phoneNumber") &&
      !get(data, "preferences.contactInfo.email")
    ) {
      errorMessageMap["preferences.contactInfo.phoneNumber"] =
        "Phone number or Email is Required.";
      errorMessageMap["preferences.contactInfo.email"] =
        "Phone number or Email is Required.";
      isValid = false;
    }
    if (!isValid) {
      return { isValid, errorMessageMap };
    }
    return { isValid, dataToPost: data };
  };

  const onSaveCtaClick = () => {
    const callback = (success: boolean) => {
      if (success) {
        onModalClose();
        setApiStateModalData({
          show: true,
          modalText: isEditMode
            ? "Configuration Saved Successfully."
            : "Configuration Added Successfully.",
        });
        fetchAllOutletsData();
      }
    };
    let validatedData = validateAggregatorData();
    if (validatedData?.isValid) {
      dispatch(
        saveOutletConfiguration({
          dataToPost: validatedData?.dataToPost,
          callback,
        })
      );
    } else {
      setErrMsgMap(validatedData?.errorMessageMap);
    }
  };

  function renderTextField(textConfig: {
    label?: string;
    placeholder?: string;
    regexCheck?: any;
    onChange?: any;
    customClass?: string;
    parentClass?: string;
    value: string;
    key: string;
    maxLength?: number;
    iconClass?: string;
    rows?: number;
    isDisabled?: boolean;
  }) {
    return (
      <CustomTextField
        textBoxLabel={textConfig?.label || ""}
        placeholder={textConfig?.placeholder || ""}
        regexCheck={textConfig?.regexCheck || allowEverything}
        handleChange={(val: string) => handleInputChange(val, textConfig?.key)}
        customClass={`min-h-[44px] flex items-center px-16 text-16 font-normal ${
          textConfig?.customClass || ""
        }`}
        parentClass={textConfig?.parentClass || ""}
        value={textConfig?.value || ""}
        rows={textConfig?.rows || 1}
        isMandatory={
          !!AGGREGATOR_CONFIG_MADATORY_FIELDS_MAP[aggregatorConfig?.type]?.[
            textConfig?.key
          ]
        }
        maxLength={textConfig?.maxLength || 10}
        errorMessage={errorMsgMap[textConfig?.key] || ""}
        iconClass={textConfig?.iconClass}
        isDisabled={textConfig?.isDisabled || false}
      ></CustomTextField>
    );
  }

  function renderCustomDropDown(dropdownConfig: {
    label?: string;
    optionsMap: any;
    isMultiSelect?: boolean;
    placeholder?: string;
    getListItemText?: any;
    parentClass?: string;
    dropdownHeading?: string;
    searchPlaceholder?: string;
    onSubmit: any;
    preSelectedValues: any;
    uniqueKey?: any;
    key: string;
    isDisabled?: boolean;
  }): any {
    return (
      <CustomDropdown
        textBoxLabel={dropdownConfig.label}
        optionsMap={dropdownConfig.optionsMap}
        isMultiSelect={dropdownConfig.isMultiSelect || false}
        placeholder={dropdownConfig.placeholder}
        getListItemText={(data: any) =>
          dropdownConfig.getListItemText
            ? dropdownConfig.getListItemText(data)
            : data?.name || ""
        }
        parentClass={`mb-[6px] ${
          dropdownConfig?.isDisabled ? "opacity-[0.5]" : ""
        } ${dropdownConfig.parentClass}`}
        dropdownHeading={dropdownConfig.dropdownHeading}
        searchPlaceholder={dropdownConfig.searchPlaceholder}
        onSubmit={dropdownConfig.onSubmit}
        preSelectedValues={dropdownConfig.preSelectedValues || ""}
        uniqueKey={dropdownConfig.uniqueKey || "mid"}
        errorMessage={errorMsgMap[dropdownConfig.key] || ""}
        isMandatory={
          !!AGGREGATOR_CONFIG_MADATORY_FIELDS_MAP[aggregatorConfig?.type]?.[
            dropdownConfig?.key
          ]
        }
        isDisabled={dropdownConfig?.isDisabled || false}
      ></CustomDropdown>
    );
  }

  function renderSwitchComponent(config: {
    label: string;
    key: string;
    onVal?: any;
    offVal?: any;
  }) {
    const { label, key, onVal = true, offVal = false } = config;
    let value = get(aggregatorConfig, key) === onVal ? true : false;
    return (
      <div className="flex items-center mb-[28px]">
        <p className="text-16 font-medium mr-[10px]">{label}</p>
        <p
          className={`text-14 ${
            !!value ? "text-[#0074FB]" : "text-[#C7C7C7]"
          } font-medium w-[30px] mr-[4px]`}
        >
          {!!value ? "ON" : "OFF"}
        </p>
        <SwitchComponent
          isChecked={!!value}
          inputProps={{ "aria-label": "ant design" }}
          onSwitchClick={(val: any) =>
            handleInputChange(val ? onVal : offVal, key)
          }
        />
      </div>
    );
  }

  function renderModalHeader() {
    return (
      <div
        className={`flex items-center justify-between py-[12px] px-[24px] absolute top-0
          w-full sm:px-[16px]
        `}
      >
        <p className="text-22 font-semibold">
          {isEditMode
            ? `Edit Aggregator - ${outletData?.channelDefName}`
            : "Add New Online Aggregator"}
        </p>
        <div
          className="rista-sprite ic-cross-black"
          onClick={onModalClose}
        ></div>
      </div>
    );
  }

  function renderActionFooter() {
    return (
      <div
        className={`absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px]
          px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden
          sm:w-full sm:px-[16px]
        `}
      >
        <CtaComponent
          ctaText={"Discard"}
          ctaType={CTA_TYPE.secondary_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onModalClose}
          customClass={"mr-10 sm:mr-[8px]"}
        ></CtaComponent>
        <CtaComponent
          ctaText={"Save"}
          ctaType={CTA_TYPE.active_cta}
          ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
          onCtaClick={onSaveCtaClick}
          customClass={"ml-10 sm:ml-[8px]"}
          isDisabled={!isChangesMade}
        ></CtaComponent>
      </div>
    );
  }

  function renderAggregators() {
    return (
      <div className="flex items-center flex-wrap gap-[20px] px-[24px] mt-[18px]">
        {Object.keys(AGGREGATOR_TYPE).map((id: string) => {
          return (
            <div
              className={`w-[120px] h-[120px] p-[12px] rounded-[9px] border-[2px] border-[#C7C7C7] cursor-pointer
                flex flex-col items-start justify-between hover:border-[#397EF5] hover:border-[2px]
                ${aggregatorConfig?.type === id ? "bg-[#0074FB]/10" : ""}
              `}
              onClick={() => handleInputChange(id, "type")}
              key={id}
            >
              <img
                src={AGGREGATOR_TYPE[id]?.logo}
                alt="aggregator-logo"
                className=""
              />
              <h1 className="text-16 font-semibold leading-none">
                {AGGREGATOR_TYPE[id]?.label}
              </h1>
            </div>
          );
        })}
      </div>
    );
  }

  function renderBusinessHours() {
    let key =
      aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id
        ? "preferences.businessHours.businessDays"
        : "businessHours";
    return (
      <div className="">
        <div className="mb-[16px] flex items-center justify-between">
          <p className="text-16">Business Hours</p>
          <button
            className="text-14 text-[#0074FB] font-semibold cursor-pointer border-0 bg-white sm:text-[14px]"
            onClick={() => setTimeSlotEditModalData({ show: true })}
          >
            + Add
          </button>
        </div>
        {get(aggregatorConfig, key)?.length
          ? get(aggregatorConfig, key)?.map((businessDay: any, index: any) => {
              return businessDay?.slots?.length ? (
                <div
                  className="border border-[#EBEBEB] rounded-[8px] mb-[12px] last:mb-0"
                  key={`businessDay-item-${index}`}
                >
                  <div className="flex items-center justify-between px-[16px] py-[10px] rounded-t-[8px] border-b border-[#EBEBEB] bg-[#F1F7FE]">
                    <p className="text-16 font-semibold">
                      {
                        BUSINESS_DAYS_NAME_MAP[businessDay?.day?.toLowerCase()]
                          ?.longName
                      }
                    </p>
                    <div className="flex items-center">
                      <div
                        className="rista-sprite ic-pencil-grey cursor-pointer mr-[18px]"
                        onClick={() =>
                          onTimeSlotEditIconClick(
                            businessDay?.day,
                            businessDay?.slots
                          )
                        }
                      ></div>
                      <div
                        className="rista-sprite ic-delete-grey cursor-pointer"
                        onClick={() =>
                          onTimeSlotDeleteIconClick(businessDay?.day)
                        }
                      ></div>
                    </div>
                  </div>
                  <div className="px-[16px] py-[14px]">
                    {businessDay?.slots?.length
                      ? businessDay?.slots.map((slot: any, index: number) => {
                          return (
                            <span
                              key={`timelines-${index}`}
                              className="min-w-fit text-16 font-normal leading-none"
                            >{`${index > 0 ? ", " : ""}${tConvert(
                              slot?.startTime
                            )} - ${tConvert(slot?.endTime, true)}`}</span>
                          );
                        })
                      : null}
                  </div>
                </div>
              ) : null;
            })
          : null}
      </div>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <div
        className={`outlet-logistic-status-modal absolute w-[60vw] max-w-[636px] rounded-[8px]
          overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white pt-[70px] pb-[84px]
          sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
          sm:w-full sm:h-[90%] sm:rounded-b-[0]
        `}
      >
        {renderModalHeader()}
        {aggregatorConfig?.type || isEditMode ? (
          <>
            <div className="md:h-[calc(80vh-154px)] md:max-h-[514px] md:min-h-[20vh] sm:h-full overflow-y-auto">
              <div
                className={`flex items-center px-[24px] pb-[16px] mt-[14px] border-b border-black/[0.14]
                  sm:px-[16px]
                `}
              >
                <img
                  src={AGGREGATOR_TYPE[aggregatorConfig?.type]?.logo}
                  alt="aggregator-logo"
                  className="mr-[14px]"
                />
                <h1 className="text-16 font-semibold leading-none mr-[16px] sm:text-[16px]">
                  {AGGREGATOR_TYPE[aggregatorConfig?.type]?.label}
                </h1>
                {isEditMode ? null : (
                  <p
                    className="text-14 text-[#0074FB] font-medium cursor-pointer"
                    onClick={() => handleInputChange("", "type")}
                  >
                    Change
                  </p>
                )}
              </div>
              <div className="px-[24px] mt-[16px] sm:px-[16px]">
                {renderTextField(TEXT_FIELD_CONFIG.outletId)}
                {renderCustomDropDown(DROPDOWN_CONFIGS.branch)}
                {renderCustomDropDown(DROPDOWN_CONFIGS.channel)}
                {aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id ? null : (
                  <>
                    {renderTextField(TEXT_FIELD_CONFIG.externalOutletId)}
                    {renderTextField(TEXT_FIELD_CONFIG.externalOutletUrl)}
                  </>
                )}

                {aggregatorConfig?.type === AGGREGATOR_TYPE.urbanpiper.id
                  ? renderTextField(TEXT_FIELD_CONFIG.apiKey)
                  : null}

                {aggregatorConfig?.type === AGGREGATOR_TYPE.webhook.id ? (
                  <>
                    {renderCustomDropDown(DROPDOWN_CONFIGS.webhookEntity)}
                    {renderCustomDropDown(DROPDOWN_CONFIGS.webhookSource)}
                  </>
                ) : null}
                {aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id
                  ? null
                  : renderSwitchComponent({
                      label: "Auto Confirm",
                      key: "autoConfirm",
                      onVal: "1",
                      offVal: "0",
                    })}
                {renderSwitchComponent({
                  label: "Auto Process",
                  key: "autoProcess",
                  onVal: 1,
                  offVal: 0,
                })}
                {aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id
                  ? null
                  : renderBusinessHours()}
              </div>
              {aggregatorConfig?.type === AGGREGATOR_TYPE.rista.id ? (
                <RistaPreferences
                  handleInputChange={handleInputChange}
                  renderBusinessHours={renderBusinessHours}
                  aggregatorConfig={aggregatorConfig}
                  paymentInfoList={paymentInfoList}
                  setPaymentInfoList={setPaymentInfoList}
                  paymentProvidersMap={paymentProvidersMap}
                  DELIVERY_MODES={DELIVERY_MODES}
                  renderCustomDropDown={renderCustomDropDown}
                  renderSwitchComponent={renderSwitchComponent}
                  renderTextField={renderTextField}
                />
              ) : null}
              {metaProvidersDataMap[aggregatorConfig?.type]?.website ? (
                <div
                  className={`border border-[#0074FB] p-[14px] flex
                    items-center justify-center rounded-[8px] mx-[24px] cursor-pointer mt-[24px] sm:mx-[16px]
                  `}
                  onClick={() =>
                    window.open(
                      metaProvidersDataMap[aggregatorConfig?.type]?.website,
                      "_blank",
                      "noopener,noreferrer"
                    )
                  }
                >
                  <div className="rista-sprite ic-url-globe-blue mr-[10px]"></div>
                  <p className="text-16 text-[#0074FB] leading-none">
                    Click Here for web URL
                  </p>
                </div>
              ) : null}
            </div>

            {renderActionFooter()}
          </>
        ) : (
          renderAggregators()
        )}
        {timeSlotEditModalData.show && (
          <TimelineManagementModal
            daySelected={timeSlotEditModalData?.day}
            isTimelineEditMode={timeSlotEditModalData?.isEditMode}
            curDayTimeSlots={timeSlotEditModalData?.slots}
            onModalClose={onTimeSlotEditModalClose}
            onSaveCtaClick={onTimeSlotEditModalSave}
            tConvert={tConvert}
          />
        )}
      </div>
    </Modal>
  );
};

export default OutletConfigurationEditModal;
